// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from "react";
import "./TimelineCardNew.scss";

import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";

import ContaminantsTimeline from "../../AggregationsTimeline/ContaminantsTimeline";
import BrownPaperTimeline from "../../AggregationsTimeline/BrownPaperTimeline";
import VolumeTimeline from "../../AggregationsTimeline/VolumeTimeline";
import MaterialDistributionTimeline from "../../AggregationsTimeline/MaterialDistributionTimeline";
import BaleCountTimeline from "../../AggregationsTimeline/BaleCountTimeline";
import TimelineSettings from "./TimelineSettings";
import MaterialFlowModal from "../../MaterialFlowModal/MaterialFlowModal";

import cardIcon from "../../../assets/icons/card_icons/icon_plant_process.png";

export default function TimelineCardNew({timeframe}) {
    const { i18n, t } = useTranslation();
    const { currentUser, dashboardSettings } = React.useContext(AuthContext);
    const [ materialFlowViewTimestamp, setMaterialFlowViewTimestamp ] = React.useState(null);

    // Define the available timelines, then filter out the ones that don't have all labelset-dependencies met
    function getTimelineConfigs(timeframe) {
        const daysTimeframe = Math.ceil((timeframe.endDate - timeframe.startDate) / (1000 * 60 * 60 * 24));

        const availableLabelsets = Object.entries(currentUser?.devices?.[currentUser.device_id]?.available_labelsets || {})
        .filter(([key, value]) => value?.available)
        .map(([key, value]) => key);

        return Object.entries({
            "bale-count": {
                name: t("timeline_bale_count_name"),
                component: BaleCountTimeline,
                props: ["timeIntervalMinutes", "showSuppliers"],
                defaultProps: {
                    timeIntervalMinutes: daysTimeframe <= 3 ? 60 : 1440,
                },
                labelsetDependencies: []
            },
            
            "material-distribution": {
                name: t("timeline_material_distribution_name"),
                component: MaterialDistributionTimeline,
                props: ["timeIntervalMinutes", "useEMA", "EMAPeriod", "useNormalized", "labelset"],
                defaultProps: {
                    timeIntervalMinutes: daysTimeframe <= 3 ? 5 : daysTimeframe <= 28 ? 15 : 1440,
                    useEMA: daysTimeframe <= 28 ? true : false,
                    EMAPeriod: 12,
                    labelset: "materials"
                },
                labelsetDependencies: ["materials"]
            },
            "plastics-type-distribution": {
                name: t("timeline_plastics_type_distribution_name"),
                component: MaterialDistributionTimeline,
                props: ["timeIntervalMinutes", "useEMA", "EMAPeriod", "useNormalized", "labelset"],
                defaultProps: {
                    timeIntervalMinutes: daysTimeframe <= 3 ? 60 : 1440,
                    useEMA: false,
                    labelset: "plastics_type"
                },
                labelsetDependencies: ["plastics_type"]
            },
            "contaminants": {
                name: t("timeline_contaminants_name"),
                component: ContaminantsTimeline,
                props: ["timeIntervalMinutes", "showSuppliers"],
                defaultProps: {
                    timeIntervalMinutes: daysTimeframe <= 3 ? 60 : 1440
                },
                labelsetDependencies: ["contaminants"]
            },
            "brown-paper": {
                name: t("timeline_brown_paper_name"),
                component: BrownPaperTimeline,
                props: ["timeIntervalMinutes", "showSuppliers", "useEMA", "EMAPeriod"],
                defaultProps: {
                    timeIntervalMinutes: daysTimeframe <= 3 ? 5 : daysTimeframe <= 28 ? 15 : 1440,
                    useEMA: daysTimeframe <= 28 ? true : false,
                    EMAPeriod: 12,
                },
                labelsetDependencies: ["brown_paper_content"]
            },
            "volume": {
                name: t("timeline_volume_flow_name"),
                component: VolumeTimeline,
                props: ["timeIntervalMinutes", "showSuppliers"],
                defaultProps: {
                    timeIntervalMinutes: daysTimeframe <= 3 ? 60 : 1440,
                },
                labelsetDependencies: []
            }
        })
        .filter(([key, value]) => value.labelsetDependencies.every(dep => availableLabelsets.includes(dep)))
        .filter(([key, value]) => dashboardSettings?.activatedGraphs?.[key] !== false)
        .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {});
    }

    const [ timelineConfigs, setTimelineConfigs ] = React.useState(getTimelineConfigs(timeframe));
    const [ chartType, setChartType ] = React.useState(Object.keys(timelineConfigs)[0]);
    const [ timelineProps, setTimelineProps ] = React.useState({
        useEMA: true,
        EMAPeriod: 12,
        useNormalized: false,
        timeIntervalMinutes: 5,
        showSuppliers: false,
        ...timelineConfigs[chartType].defaultProps
    });

    React.useEffect(() => {
        // Reset the default props if the timeInterval changes
        const newTimelineConfigs = getTimelineConfigs(timeframe);

        const newDefaultProps = newTimelineConfigs[chartType].defaultProps;
        const oldDefaultProps = timelineConfigs[chartType].defaultProps;
        const changedDefaultProps = {};
        Object.entries(newDefaultProps).forEach(([key, value]) => {
            if (key in oldDefaultProps && value === oldDefaultProps[key]) {
                return;
            }
            changedDefaultProps[key] = value;
        });

        if (Object.keys(changedDefaultProps).length > 0) {
            setTimelineProps(prev => ({...prev, ...changedDefaultProps}));
        }

        setTimelineConfigs(newTimelineConfigs);
    }, [timeframe])

    function switchTimeline(timelineType) {
        setTimelineProps(prev => ({...prev, ...timelineConfigs[timelineType].defaultProps}));
        setChartType(timelineType);
    }

    function handlePropChange(propName, value) {
        setTimelineProps(prev => ({...prev, [propName]: value}));
    }

    function getTimelineComponent() {
        const TimelineComponent = timelineConfigs[chartType].component;
        const relevantProps = timelineConfigs[chartType].props;
        const filteredProps = Object.keys(timelineProps)
            .filter(key => relevantProps.includes(key))
            .reduce((obj, key) => {
                obj[key] = timelineProps[key];
                return obj;
            }, {});
        return <TimelineComponent timeframe={timeframe} onTimeSelected={onTimeSelected} {...filteredProps} />;
    }

    function onTimeSelected(timestamp) {
        setMaterialFlowViewTimestamp(timestamp);
    }

    return (
        <div className="timeline-chart-container">
            <div className="timeline-header">
                <div className="iconWrapper">
                    <img src={cardIcon} alt="Timeline Icon" />
                </div>
                <div className="title-section">
                    <span className="title">{t('timeline_card_title')}</span>
                </div>
                <div className="chart-toggle-buttons">
                    {Object.keys(timelineConfigs).map(key => (
                        <button 
                            className={chartType === key ? "selected" : ""} 
                            onClick={() => switchTimeline(key)}
                        >
                            {timelineConfigs[key].name}
                        </button>
                    ))}
                    <div className="button-separator"></div>
                    <TimelineSettings 
                        timelineProps={timelineProps}
                        onPropsChange={handlePropChange}
                        currentTimeline={timelineConfigs[chartType]}
                    />
                </div>
            </div>
            {getTimelineComponent()}
            {materialFlowViewTimestamp && (
            <MaterialFlowModal 
                isOpen={materialFlowViewTimestamp !== null}
                onClose={() => setMaterialFlowViewTimestamp(null)}
                timestamp={materialFlowViewTimestamp}
            />)}
        </div>
    )
}